import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { provideRouter, Router, withComponentInputBinding, withViewTransitions } from '@angular/router';
import { routes } from './app/app.routes';
import {
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { MCFormlyValidationConfig, MCConfigService, MCTranslationStateService, MCFieldsCustomValidator } from '@maslul/common-services';
import { environment } from './environments/environment';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FORMLY_CONFIG, FormlyModule } from '@ngx-formly/core';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';
import { customFormlyConfig } from './assets/config/formly.config';
import { CustomTranslateLoader } from './assets/i18n/CustomTranslateLoader';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { Interceptor } from './app/services/interceptor/interceptor.service';
import { CustomAsyncValidatorFunctions } from './app/services/fields/custom-async-validation-functions';
import { AUTHORITIES_PROVIDERS } from './app/providers/authorities.providers';
import { CORE_PROVIDERS } from './app/providers/core.providers';

// Register async validators immediately (before bootstrap)
MCFieldsCustomValidator.registerAsyncValidatorFunctions(CustomAsyncValidatorFunctions as any);

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});
export function ConfigLoader(configService: MCConfigService) {
  return () => configService.load(environment.configFile);
}

export function CustomLoaderFactory(
  http: HttpClient,
  translationStateService: MCTranslationStateService,
) {
  return new CustomTranslateLoader(http, translationStateService);
}

function initializeApp(
  router: Router,
  translationStateService: MCTranslationStateService,
): () => Promise<any> {
  return () => {
    return new Promise<void>((resolve) => {
      translationStateService.setTranslationsLoaded(false);
      resolve();
    });
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          cssLayer: {
            name: 'primeng',
          },
        },
      },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideRouter(routes, withComponentInputBinding()),

    ...CORE_PROVIDERS,
    ...AUTHORITIES_PROVIDERS,

    provideAppInitializer(() => {
      const initializerFn = ConfigLoader(inject(MCConfigService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: Interceptor,
      multi: true,
      deps: [Injector],
    },
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(Router),
        inject(MCTranslationStateService),
      );
      return initializerFn();
    }),

    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: CustomLoaderFactory,
          deps: [HttpClient, MCTranslationStateService],
        },
      }),
      FormlyModule.forRoot(customFormlyConfig),
      FormlyPrimeNGModule,
    ),
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: MCFormlyValidationConfig,
      deps: [TranslateService],
    },
  ],
}).catch((err) => console.error(err));
